import React, { useState, useEffect, useRef } from 'react';
import { User, Building2, Calendar, FileText, Scale, MapPin, ChevronDown, Plus, Trash2, ReceiptEuro, Wallet, Euro, CreditCard, Clock, FolderOpen, StickyNote } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import DatePicker, { registerLocale } from 'react-datepicker';
import el from 'date-fns/locale/el';
import 'react-datepicker/dist/react-datepicker.css';
import DatePickerWithInput from './DatePickerWithInput';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import PostponementWizard from './PostponementWizard';
import OtherActionForm, { OTHER_ACTION_OPTIONS } from './OtherActionForm';
import CaseDocuments from './CaseDocuments';

// Register Greek locale
registerLocale('el', el);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Court locations list
const courtLocations = [
  'Αγρίνιο', 'Αθήνα', 'Αλεξανδρούπολη', 'Άμφισσα', 'Άργος', 'Άρτα', 'Βέροια', 'Βόλος',
  'Γρεβενά', 'Δράμα', 'Έδεσσα', 'Ζάκυνθος', 'Ηγουμενίτσα', 'Ηράκλειο', 'Θήβα', 'Θεσσαλονίκη',
  'Ιωάννινα', 'Καβάλα', 'Καλαμάτα', 'Καλάμπακα', 'Καρδίτσα', 'Καστοριά', 'Κατερίνη', 'Κέρκυρα',
  'Κιλκίς', 'Κοζάνη', 'Κομοτηνή', 'Κόρινθος', 'Κως', 'Λαμία', 'Λάρισα', 'Λασίθι', 'Λευκάδα',
  'Λιβαδειά', 'Μεσολόγγι', 'Μυτιλήνη', 'Ναύπλιο', 'Ξάνθη', 'Πάτρα', 'Πειραιάς', 'Πολύγυρος',
  'Πρέβεζα', 'Πύργος', 'Ρέθυμνο', 'Ρόδος', 'Σέρρες', 'Σύρος', 'Σπάρτη', 'Τρίκαλα', 'Τρίπολη',
  'Φλώρινα', 'Χαλκίδα', 'Χανιά', 'Χίος'
];

// Helper function to convert to uppercase and remove Greek accents
const toUpperCaseNoAccents = (str) => {
  if (!str) return '';
  const accentMap = {
    'ά': 'Α', 'έ': 'Ε', 'ή': 'Η', 'ί': 'Ι', 'ό': 'Ο', 'ύ': 'Υ', 'ώ': 'Ω',
    'Ά': 'Α', 'Έ': 'Ε', 'Ή': 'Η', 'Ί': 'Ι', 'Ό': 'Ο', 'Ύ': 'Υ', 'Ώ': 'Ω',
    'ϊ': 'Ι', 'ϋ': 'Υ', 'ΐ': 'Ι', 'ΰ': 'Υ', 'Ϊ': 'Ι', 'Ϋ': 'Υ'
  };
  return str.toUpperCase().split('').map(char => accentMap[char] || char).join('');
};

// Helper function to capitalize only first letter of first word (keep accents)
const capitalizeFirst = (str) => {
  if (!str) return '';
  const s = str.trim();
  let result;
  if (s === s.toUpperCase()) {
    result = s.toLowerCase();
  } else {
    // Μικτή γραφή: διατήρησε λέξεις γραμμένες ΟΛΟΚΕΦΑΛΑΙΑ (ονόματα πελατών, ακρωνύμια)
    result = s.split(/(\s+)/).map((w) => {
      const letters = w.replace(/[^\p{L}]/gu, '');
      if (letters.length >= 2 && letters === letters.toUpperCase()) return w;
      return w.toLowerCase();
    }).join('');
  }
  result = result.charAt(0).toUpperCase() + result.slice(1);
  // Preserve capitalization for known proper nouns (Greek-aware: \b doesn't work with Greek)
  const properNouns = ['Κτηματολόγιο', 'Άρειος Πάγος', 'ΣτΕ', 'Ελλάδα'];
  properNouns.forEach((noun) => {
    const re = new RegExp(noun, 'gi');
    result = result.replace(re, noun);
  });
  return result;
};

// Status options (values in uppercase for DB, labels in lowercase for display)
const STATUS_OPTIONS = [
  { value: 'ΣΕ ΑΝΑΜΟΝΗ', label: 'Σε αναμονή' },
  { value: 'ΟΛΟΚΛΗΡΩΜΕΝΗ', label: 'Ολοκληρωμένη' },
  { value: 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ', label: 'Προγραμματισμός' },
];

const HEARING_STATUS_OPTIONS = [
  { value: 'ΣΕ ΑΝΑΜΟΝΗ', label: 'Σε αναμονή' },
  { value: 'ΣΥΖΗΤΗΘΗΚΕ', label: 'Συζητήθηκε' },
  { value: 'ΑΝΑΒΟΛΗ', label: 'Αναβολή' },
  { value: 'ΜΑΤΑΙΩΣΗ', label: 'Ματαίωση' },
  { value: 'ΠΑΡΑΙΤΗΣΗ ΑΠΟ ΔΙΚΟΓΡΑΦΟ', label: 'Παραίτηση' },
];

// Case description types mapping
const CASE_DESCRIPTION_LABELS = {
  'debt': 'Αγωγή χρέους',
  'property': 'Εμπράγματη αγωγή',
  'fraud': 'Αγωγή καταδολίευσης',
  'inheritance': 'Κληρονομική διαφορά',
  'divorce': 'Διαζύγιο',
  'alimony': 'Διατροφή',
  'custody': 'Επιμέλεια τέκνων',
  'lease': 'Μισθωτική διαφορά',
  'labor': 'Εργατική διαφορά',
  'tort': 'Αδικοπραξία',
  'contract': 'Συμβατική διαφορά',
  'unjust_enrichment': 'Αδικαιολόγητος πλουτισμός',
  'compensation': 'Αποζημίωση',
  'ownership': 'Αναγνώριση κυριότητας',
  'possession': 'Προστασία νομής',
  'easement': 'Δουλεία',
  'condominium': 'Διαφορά οροφοκτησίας',
  'insurance': 'Ασφαλιστική διαφορά',
  'medical': 'Ιατρική αμέλεια',
  'traffic': 'Τροχαίο ατύχημα',
  'other': 'Άλλο'
};

// Options for new entry by procedure type
const CIVIL_OTHER_OPTIONS = [
  { value: 'hearing_date', label: 'Ορισμός ημερομηνίας δικασίμου (άρθρο 237 ΚΠολΔ)' },
  { value: 'postponement', label: 'Αναβολή συζήτησης' },
  { value: 'sworn_statement', label: 'Λήψη ένορκης βεβαίωσης' },
  { value: 'cancellation', label: 'Ματαίωση συζήτησης' },
  { value: 'withdrawal', label: 'Παραίτηση από δικόγραφο' },
  { value: 'final_decision_service', label: 'Επίδοση οριστικής απόφασης' },
  { value: 'final_decision_publication', label: 'Δημοσίευση οριστικής απόφασης' },
  { value: 'final_rejection', label: 'Τελεσίδικη απόρριψη αγωγής για τυπικούς λόγους' },
  { value: 'payment_order_issue', label: 'Έκδοση διαταγής πληρωμής' },
  { value: 'payment_order_service', label: 'Επίδοση διαταγής πληρωμής ή απόδοσης μισθίου' },
  { value: 'katataxi_prosklisi', label: 'Επίδοση πρόσκλησης για πίνακα κατάταξης' },
  { value: 'diataxi_prodikastiki', label: 'Έκδοση Διάταξης προδικαστικού ελέγχου (237 ΚΠολΔ)' },
  { value: 'enforcement_action', label: 'Πράξη αναγκαστικής εκτέλεσης' },
];

const CRIMINAL_OTHER_OPTIONS = [
  { value: 'postponement', label: 'Αναβολή συζήτησης' },
  { value: 'decision', label: 'Έκδοση ή επίδοση απόφασης ποινικού δικαστηρίου' },
];

const ADMINISTRATIVE_OTHER_OPTIONS = [
  { value: 'postponement', label: 'Αναβολή συζήτησης' },
  { value: 'first_instance_service', label: 'Επίδοση οριστικής απόφασης Διοικητικού Πρωτοδικείου' },
  { value: 'first_instance_publication', label: 'Δημοσίευση οριστικής απόφασης Διοικητικού Πρωτοδικείου' },
  { value: 'appeal_service', label: 'Επίδοση απόφασης Διοικητικού Εφετείου' },
  { value: 'appeal_publication', label: 'Δημοσίευση οριστικής απόφασης Διοικητικού Εφετείου' },
  { value: 'appeal_cancellation_service', label: 'Επίδοση απόφασης Διοικητικού Εφετείου επί αιτήσεως ακύρωσης' },
  { value: 'appeal_cancellation_publication', label: 'Δημοσίευση οριστικής απόφασης Διοικητικού Εφετείου επί αιτήσεως ακύρωσης' },
  { value: 'final_rejection', label: 'Τελεσίδικη απόρριψη αγωγής για τυπικούς λόγους' },
  { value: 'non_final_service', label: 'Επίδοση μη οριστικής απόφασης' },
  { value: 'non_final_publication', label: 'Δημοσίευση μη οριστικής απόφασης' },
  { value: 'sworn_statements', label: 'Κλήση για λήψη ενόρκων βεβαιώσεων' },
  { value: 'ste_filing', label: 'Κατάθεση δικογράφου ΣτΕ (επίδοση εντός 2 μηνών)' },
  { value: 'ste_appeal_service', label: 'Επίδοση ενδίκου μέσου ΣτΕ (πρόσθετοι λόγοι)' },
];

const EntryDetail = ({ entry, client, caseData, relatedEntries, onClose, onRefresh, onOpenNewEntryWithData }) => {
  const [office, setOffice] = useState(null);
  useEffect(() => {
    if (!entry) return;
    axios.get(`${API}/office`).then(({ data }) => setOffice(data)).catch(() => {});
  }, [entry?.id]);

  const assignableMembers = (office?.members || []).filter((m) => m.active && m.role !== 'secretary');
  const canAssign = office?.my_role === 'head' && assignableMembers.length > 1;

  const assignEntry = async (userId) => {
    try {
      await axios.put(`${API}/entries/${entry.id}/assign`, { user_id: userId || null });
      toast.success(userId ? 'Η εγγραφή χρεώθηκε' : 'Η χρέωση αφαιρέθηκε');
      onRefresh?.();
    } catch (e) {
      toast.error('Σφάλμα ανάθεσης');
    }
  };

  const assignWholeCase = async (userId) => {
    if (!caseData?.id) return;
    try {
      const { data } = await axios.put(`${API}/cases/${caseData.id}/assign`, { user_id: userId || null });
      toast.success(`Χρεώθηκε όλη η υπόθεση (${data.entries_updated} εγγραφές)`);
      onRefresh?.();
    } catch (e) {
      toast.error('Σφάλμα ανάθεσης υπόθεσης');
    }
  };

  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [newEntryType, setNewEntryType] = useState('');
  const [customEntryTitle, setCustomEntryTitle] = useState('');
  const [isCreatingEntry, setIsCreatingEntry] = useState(false);
  const [localEntries, setLocalEntries] = useState(relatedEntries || [entry]);
  const [caseTasks, setCaseTasks] = useState([]);
  const [timeDrafts, setTimeDrafts] = useState({});
  const [caseNotes, setCaseNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  const noteCaseId = caseData?.id || entry?.case_id;

  const loadCaseNotes = (cid) => {
    axios.get(`${API}/cases/${cid}/notes`)
      .then(({ data }) => setCaseNotes(data.notes || []))
      .catch(() => setCaseNotes([]));
  };

  useEffect(() => {
    if (!noteCaseId) { setCaseNotes([]); return; }
    loadCaseNotes(noteCaseId);
  }, [noteCaseId]);

  const addCaseNote = async () => {
    const text = newNoteText.trim();
    if (!text || !noteCaseId) return;
    try {
      await axios.post(`${API}/cases/${noteCaseId}/notes`, { text });
      setNewNoteText('');
      loadCaseNotes(noteCaseId);
    } catch (e) { toast.error('Σφάλμα προσθήκης σημείωσης'); }
  };

  const saveNoteEdit = async (noteId) => {
    const text = editingNoteText.trim();
    setEditingNoteId(null);
    if (!text) return;
    try {
      await axios.put(`${API}/case-notes/${noteId}`, { text });
      loadCaseNotes(noteCaseId);
    } catch (e) { toast.error('Σφάλμα αποθήκευσης σημείωσης'); }
  };

  const deleteCaseNote = async (noteId) => {
    try {
      await axios.delete(`${API}/case-notes/${noteId}`);
      setCaseNotes(caseNotes.filter((n) => n.id !== noteId));
    } catch (e) { toast.error('Σφάλμα διαγραφής σημείωσης'); }
  };

  useEffect(() => {
    const cid = caseData?.id || entry?.case_id;
    if (!cid) { setCaseTasks([]); return; }
    axios.get(`${API}/day-tasks/by-case/${cid}`)
      .then(({ data }) => setCaseTasks(data.tasks || []))
      .catch(() => setCaseTasks([]));
  }, [caseData?.id, entry?.case_id]);

  const getEntryTime = (e) => {
    const t = (e.date || '').slice(11, 16);
    return /^\d{2}:\d{2}$/.test(t) && t !== '00:00' ? t : '';
  };

  const saveHearingTime = async (e, time) => {
    if (time === getEntryTime(e)) return;
    const base = (e.date || '').slice(0, 10);
    const newDate = time ? `${base}T${time}:00` : base;
    try {
      await axios.patch(`${API}/entries/${e.id}`, { date: newDate });
      setLocalEntries((prev) => prev.map((x) => (x.id === e.id ? { ...x, date: newDate } : x)));
      toast.success(time ? `Ώρα δικασίμου: ${time}` : 'Η ώρα δικασίμου αφαιρέθηκε');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Σφάλμα αποθήκευσης ώρας');
    }
  };
  
  // Sync localEntries when relatedEntries prop changes (e.g., after postponement refresh)
  useEffect(() => {
    if (relatedEntries && relatedEntries.length > 0) {
      setLocalEntries(relatedEntries);
    }
  }, [relatedEntries]);
  
  const [pendingScheduleEntryId, setPendingScheduleEntryId] = useState(null);
  const [scheduledDateTemp, setScheduledDateTemp] = useState(null);
  const datePickerRef = useRef(null);
  const [deleteEntryId, setDeleteEntryId] = useState(null);
  const [deleteCaseConfirm, setDeleteCaseConfirm] = useState(false);
  const [isDeletingCase, setIsDeletingCase] = useState(false);
  
  // Αναβολή δικασίμου state
  const [postponementEntryId, setPostponementEntryId] = useState(null);
  const [postponementDate, setPostponementDate] = useState(null);
  const [showPostponementWizard, setShowPostponementWizard] = useState(false);
  const [postponementEntry, setPostponementEntry] = useState(null);
  
  // Financial entries state
  const [financialEntries, setFinancialEntries] = useState([]);
  const [loadingFinancial, setLoadingFinancial] = useState(false);
  const [showFinancialForm, setShowFinancialForm] = useState(null); // 'fee' or 'payment'
  const [financialFormData, setFinancialFormData] = useState({
    entry_type: 'fee',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    fee_type: 'flat',
    hours: '',
    hourly_rate: ''
  });
  
  // Edit case modal state
  const [isEditingCase, setIsEditingCase] = useState(false);
  const [originalDocumentType, setOriginalDocumentType] = useState('');
  const [changeDateSelected, setChangeDateSelected] = useState(false);
  const [editCaseForm, setEditCaseForm] = useState({
    case_description_type: '',
    case_description_custom: '',
    opponent_details: '',
    court_type: '',
    court_location: '',
    document_type: ''
  });

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setPendingScheduleEntryId(null);
        setScheduledDateTemp(null);
      }
    };
    if (pendingScheduleEntryId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [pendingScheduleEntryId]);
  
  // Update local entries when props change
  React.useEffect(() => {
    if (relatedEntries && relatedEntries.length > 0) {
      setLocalEntries(relatedEntries);
    } else if (entry) {
      setLocalEntries([entry]);
    }
  }, [relatedEntries, entry]);
  
  // Fetch financial entries for this case
  useEffect(() => {
    const fetchFinancialEntries = async () => {
      if (!entry?.case_id) {
        setFinancialEntries([]);
        return;
      }
      setLoadingFinancial(true);
      try {
        const response = await axios.get(`${API}/financial/entries`);
        const caseFinancials = response.data.filter(fe => fe.case_id === entry.case_id);
        setFinancialEntries(caseFinancials);
      } catch (error) {
        console.error('Error fetching financial entries:', error);
        setFinancialEntries([]);
      } finally {
        setLoadingFinancial(false);
      }
    };
    fetchFinancialEntries();
  }, [entry?.case_id]);
  
  // Helper to format currency (no decimals — rounded)
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0 €';
    return new Intl.NumberFormat('el-GR').format(Math.round(amount)) + ' €';
  };
  
  // Handle saving financial entry from quick form
  const handleSaveFinancialEntry = async () => {
    if (!financialFormData.description.trim()) {
      toast.error('Εισάγετε περιγραφή');
      return;
    }
    
    // Calculate final amount for hourly fee
    let finalAmount = parseFloat(financialFormData.amount);
    if (
      showFinancialForm === 'fee' &&
      financialFormData.entry_type === 'fee' &&
      financialFormData.fee_type === 'hourly'
    ) {
      const h = parseFloat(financialFormData.hours) || 0;
      const r = parseFloat(financialFormData.hourly_rate) || 0;
      finalAmount = h * r;
    }
    if (!finalAmount || finalAmount <= 0) {
      toast.error('Εισάγετε έγκυρο ποσό');
      return;
    }

    const payload = {
      entry_type: showFinancialForm === 'payment' ? 'payment' : financialFormData.entry_type,
      amount: finalAmount,
      description: financialFormData.description,
      date: financialFormData.date,
      client_id: client?.id || null,
      case_id: entry?.case_id || null,
      fee_type: financialFormData.entry_type === 'fee' ? financialFormData.fee_type : null,
      hours: financialFormData.hours ? parseFloat(financialFormData.hours) : null,
      hourly_rate: financialFormData.hourly_rate ? parseFloat(financialFormData.hourly_rate) : null,
      is_paid: showFinancialForm === 'payment' // payments are automatically marked as paid
    };
    
    try {
      await axios.post(`${API}/financial/entries`, payload);
      toast.success(showFinancialForm === 'payment' ? 'Η πληρωμή καταχωρήθηκε' : 'Η χρέωση καταχωρήθηκε');
      
      // Refresh financial entries
      const response = await axios.get(`${API}/financial/entries`);
      const caseFinancials = response.data.filter(fe => fe.case_id === entry.case_id);
      setFinancialEntries(caseFinancials);
      
      // Reset form
      setShowFinancialForm(null);
      setFinancialFormData({ entry_type: 'fee', amount: '', description: '', date: new Date().toISOString().split('T')[0], fee_type: 'flat', hours: '', hourly_rate: '' });
    } catch (error) {
      console.error('Error saving financial entry:', error);
      toast.error('Σφάλμα αποθήκευσης');
    }
  };
  
  if (!entry) return null;
  
  // Handle opening edit case modal
  const handleEditCase = () => {
    setEditCaseForm({
      case_description_type: entry.case_description_type || '',
      case_description_custom: entry.case_description_custom || '',
      opponent_details: entry.opponent_details || '',
      court_type: caseData?.court_type || '',
      court_location: caseData?.court_location || '',
      document_type: caseData?.document_type || '',
      mediation_required: caseData?.mediation_required === true
    });
    setOriginalDocumentType(caseData?.document_type || '');
    setChangeDateSelected(false);
    setIsEditingCase(true);
  };

  // Handle saving case edits
  const handleSaveCase = async () => {
    // Check if document type has changed - redirect to new entry form
    if (editCaseForm.document_type !== originalDocumentType && onOpenNewEntryWithData) {
      // Redirect to new entry form with pre-filled data
      const prefillData = {
        clientId: client?.id,
        clientOption: 'existing',
        opponent_details: editCaseForm.opponent_details,
        document_type: editCaseForm.document_type,
        court_type: editCaseForm.court_type,
        court_location: editCaseForm.court_location,
        case_description_type: editCaseForm.case_description_type,
        case_description_custom: editCaseForm.case_description_custom,
        procedure_type: caseData?.procedure_type || 'civil',
        // Pass old entries and case to be deleted after new entries are created
        replaceOldEntries: true,
        oldEntryIds: localEntries.map(e => e.id),
        oldCaseId: caseData?.id
      };
      setIsEditingCase(false);
      onClose();
      onOpenNewEntryWithData(prefillData);
      return;
    }
    
    try {
      // Update all entries for this case with the new description and opponent details
      const updatePromises = localEntries.map(e => 
        axios.patch(`${API}/entries/${e.id}`, {
          case_description_type: editCaseForm.case_description_type,
          case_description_custom: editCaseForm.case_description_custom,
          opponent_details: editCaseForm.opponent_details
        })
      );
      
      // Update case data if exists
      if (caseData?.id) {
        updatePromises.push(
          axios.put(`${API}/cases/${caseData.id}`, {
            ...caseData,
            court_type: editCaseForm.court_type,
            court_location: editCaseForm.court_location,
            document_type: editCaseForm.document_type,
            mediation_required: editCaseForm.mediation_required === true
          })
        );
      }
      
      await Promise.all(updatePromises);
      toast.success('Τα στοιχεία υπόθεσης ενημερώθηκαν');
      setIsEditingCase(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating case:', error);
      toast.error('Σφάλμα κατά την ενημέρωση');
    }
  };
  
  const isHearingEntry = (entryToCheck) => {
    // Πρώτα έλεγχος entry_type
    if (entryToCheck.entry_type === 'hearing') return true;
    
    if (!entryToCheck.title) return false;
    // Αφαίρεση τόνων και σύγκριση τίτλου
    const normalizedTitle = entryToCheck.title
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    // Also compare without NFD normalization for Greek uppercase
    const uppercaseTitle = entryToCheck.title.toUpperCase();
    const isMatch = normalizedTitle === 'ΔΙΚΑΣΙΜΟΣ' || uppercaseTitle === 'ΔΙΚΆΣΙΜΟΣ' || uppercaseTitle === 'ΔΙΚΑΣΙΜΟΣ';
    return isMatch;
  };
  
  const getStatusOptions = (entryToCheck) => {
    return isHearingEntry(entryToCheck) ? HEARING_STATUS_OPTIONS : STATUS_OPTIONS;
  };
  
  const handleStatusChange = async (entryId, newStatus) => {
    // If ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ is selected, show date picker first
    if (newStatus === 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ') {
      setPendingScheduleEntryId(entryId);
      setScheduledDateTemp(null);
      setOpenStatusDropdown(null);
      return;
    }
    
    setUpdatingStatus(entryId);
    try {
      await axios.patch(`${API}/entries/${entryId}/status`, { status: newStatus });
      toast.success('Η κατάσταση ενημερώθηκε');
      setOpenStatusDropdown(null);
      // Update local state immediately for instant UI feedback
      setLocalEntries(prev => prev.map(e => 
        e.id === entryId ? { ...e, status: newStatus, scheduled_date: null } : e
      ));
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Σφάλμα κατά την ενημέρωση');
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Ενημέρωση κατάστασης δικασίμου
  const handleUpdateHearingStatus = async (entryId, newStatus) => {
    // Αν επιλεγεί ΑΝΑΒΟΛΗ, ανοίγουμε το wizard αναβολής
    if (newStatus === 'ΑΝΑΒΟΛΗ') {
      const entryToPostpone = localEntries.find(e => e.id === entryId);
      setPostponementEntry(entryToPostpone);
      setShowPostponementWizard(true);
      setOpenStatusDropdown(null);
      return;
    }
    
    // Για τις άλλες καταστάσεις (Συζήτηση, Ματαίωση, Παραίτηση), απλή αλλαγή
    try {
      await axios.patch(`${API}/entries/${entryId}/status`, { status: newStatus });
      setOpenStatusDropdown(null);
      
      // ΠΑΡΑΙΤΗΣΗ/ΜΑΤΑΙΩΣΗ: Backend deletes linked deadlines, refresh everything
      if (newStatus === 'ΠΑΡΑΙΤΗΣΗ ΑΠΟ ΔΙΚΟΓΡΑΦΟ' || newStatus === 'ΜΑΤΑΙΩΣΗ') {
        toast.success(newStatus === 'ΠΑΡΑΙΤΗΣΗ ΑΠΟ ΔΙΚΟΓΡΑΦΟ' 
          ? 'Παραίτηση καταχωρήθηκε - οι συνδεδεμένες προθεσμίες διαγράφηκαν'
          : 'Ματαίωση καταχωρήθηκε - προθεσμία 60 ημερών για νέο προσδιορισμό (260 παρ. 2 ΚΠολΔ)');
        if (onRefresh) await onRefresh();
        // Update localEntries to reflect deletions
        const remainingEntries = localEntries.filter(e => {
          if (e.id === entryId) return true; // Keep the hearing itself
          if (e.status === 'ΟΛΟΚΛΗΡΩΜΕΝΗ') return true; // Keep completed
          if (e.entry_type === 'hearing') return true; // Keep other hearings
          return false; // Remove deadlines
        }).map(e => e.id === entryId ? { ...e, status: newStatus } : e);
        setLocalEntries(remainingEntries);
      } else {
        toast.success('Η κατάσταση ενημερώθηκε');
        // Update local state
        setLocalEntries(prev => prev.map(e => 
          e.id === entryId ? { ...e, status: newStatus } : e
        ));
      }
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating hearing status:', error);
      toast.error('Σφάλμα κατά την ενημέρωση');
    }
  };
  
  // Χειρισμός αναβολής δικασίμου - δημιουργία νέας δικασίμου με τη νέα ημερομηνία
  const handlePostponementDateSelect = async (date) => {
    if (!date || !postponementEntryId) return;
    
    // Format date
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    try {
      // 1. Ενημέρωση της τρέχουσας δικασίμου σε ΑΝΑΒΟΛΗ
      await axios.patch(`${API}/entries/${postponementEntryId}/status`, { status: 'ΑΝΑΒΟΛΗ' });
      
      // 2. Βρίσκουμε τα στοιχεία της τρέχουσας δικασίμου
      const currentEntry = localEntries.find(e => e.id === postponementEntryId);
      
      // 3. Δημιουργία νέας δικασίμου με τη νέα ημερομηνία
      const newHearingData = {
        entry_type: 'hearing',
        client_id: currentEntry?.client_id || entry?.client_id,
        case_id: currentEntry?.case_id || entry?.case_id,
        title: 'Δικάσιμος',
        date: formattedDate,
        priority: 'high',
        status: 'ΣΕ ΑΝΑΜΟΝΗ',
        notes: `Αναβολή από ${currentEntry?.date ? new Date(currentEntry.date).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}`
      };
      
      const response = await axios.post(`${API}/entries`, newHearingData);
      
      // 4. Update local state
      setLocalEntries(prev => [
        ...prev.map(e => e.id === postponementEntryId ? { ...e, status: 'ΑΝΑΒΟΛΗ' } : e),
        response.data
      ]);
      
      toast.success('Η αναβολή καταχωρήθηκε και δημιουργήθηκε νέα δικάσιμος');
      setPostponementEntryId(null);
      setPostponementDate(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error handling postponement:', error);
      toast.error('Σφάλμα κατά την καταχώρηση αναβολής');
    }
  };

  const handleScheduledDateSelect = async (entryId, date) => {
    if (!date) return;
    
    // Format date as YYYY-MM-DD using local timezone (not UTC)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    setUpdatingStatus(entryId);
    
    try {
      await axios.patch(`${API}/entries/${entryId}/status`, { 
        status: 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ',
        scheduled_date: formattedDate
      });
      toast.success('Η κατάσταση ενημερώθηκε');
      // Update local state
      setLocalEntries(prev => prev.map(e => 
        e.id === entryId ? { ...e, status: 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ', scheduled_date: formattedDate } : e
      ));
      setPendingScheduleEntryId(null);
      setScheduledDateTemp(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Σφάλμα κατά την ενημέρωση');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusLabel = (entryToCheck) => {
    const statusLabels = {
      'ΣΕ ΑΝΑΜΟΝΗ': 'Σε αναμονή',
      'ΟΛΟΚΛΗΡΩΜΕΝΗ': 'Ολοκληρωμένη',
      'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ': 'Προγραμματισμός',
      'ΕΚΔΙΚΑΣΗ': 'Εκδίκαση',
      'ΑΝΑΒΟΛΗ': 'Αναβολή',
      'ΜΑΤΑΙΩΣΗ': 'Ματαίωση',
      'ΠΑΡΑΙΤΗΣΗ ΑΠΟ ΔΙΚΟΓΡΑΦΟ': 'Παραίτηση',
      'ΛΗΓΜΕΝΗ': 'Ληγμένη'
    };
    
    // Check if entry has expired (date has passed and status is not completed)
    if (entryToCheck.date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const entryDate = new Date(entryToCheck.date);
      entryDate.setHours(0, 0, 0, 0);
      
      // If date has passed and status is not ΟΛΟΚΛΗΡΩΜΕΝΗ or already ΛΗΓΜΕΝΗ
      if (entryDate < today && 
          entryToCheck.status !== 'ΟΛΟΚΛΗΡΩΜΕΝΗ' && 
          entryToCheck.status !== 'ΛΗΓΜΕΝΗ' &&
          entryToCheck.status !== 'ΑΝΑΒΟΛΗ' &&
          entryToCheck.status !== 'ΜΑΤΑΙΩΣΗ' &&
          entryToCheck.status !== 'ΠΑΡΑΙΤΗΣΗ ΑΠΟ ΔΙΚΟΓΡΑΦΟ') {
        return 'Ληγμένη';
      }
    }
    
    if (!entryToCheck.status) return 'Σε αναμονή';
    
    // If ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ with scheduled_date, show "Για DD/MM/YYYY"
    if (entryToCheck.status === 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ' && entryToCheck.scheduled_date) {
      const schedDate = new Date(entryToCheck.scheduled_date);
      const dd = String(schedDate.getDate()).padStart(2, '0');
      const mm = String(schedDate.getMonth() + 1).padStart(2, '0');
      const yyyy = schedDate.getFullYear();
      return `Για ${dd}/${mm}/${yyyy}`;
    }
    
    return statusLabels[entryToCheck.status] || entryToCheck.status;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const fullDate = date.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return fullDate;
  };

  const getClientName = () => {
    if (!client) return 'ΑΓΝΩΣΤΟΣ';
    if (client.client_type === 'natural') {
      const surname = (client.surname || '').trim();
      const name = (client.name || '').trim();
      const parts = [surname, name].filter(Boolean);
      return toUpperCaseNoAccents(parts.join(' ') || 'Άγνωστος');
    }
    return toUpperCaseNoAccents(client.company_name || 'Άγνωστος');
  };

  const getCourtTypeName = (courtType) => {
    const courtTypes = {
      'single_peace': 'Μονομελές Ειρηνοδικείο', 
      'multi_peace': 'Πολυμελές Ειρηνοδικείο',
      'single_first': 'Μονομελές Πρωτοδικείο', 
      'multi_first': 'Πολυμελές Πρωτοδικείο',
      'single_appeal': 'Μονομελές Εφετείο', 
      'multi_appeal': 'Τριμελές Εφετείο',
      'triple_appeal': 'Τριμελές Εφετείο',
      'supreme': 'Άρειος Πάγος', 
      'administrative_first': 'Διοικητικό Πρωτοδικείο',
      'administrative_appeal': 'Διοικητικό Εφετείο', 
      'council_of_state': 'Συμβούλιο της Επικρατείας',
      // Διοικητικά
      'single_admin_first': 'Μονομελές Διοικητικό Πρωτοδικείο',
      'triple_admin_first': 'Τριμελές Διοικητικό Πρωτοδικείο',
      'admin_appeal': 'Διοικητικό Εφετείο',
      'triple_admin_appeal': 'Τριμελές Διοικητικό Εφετείο',
      // Ποινικά
      'single_criminal': 'Μονομελές Πλημμελειοδικείο',
      'three_criminal': 'Τριμελές Πλημμελειοδικείο',
      'single_misdemeanor': 'Μονομελές Πλημμελειοδικείο',
      'triple_misdemeanor': 'Τριμελές Πλημμελειοδικείο',
      'single_criminal_appeal': 'Μονομελές Εφετείο Κακουργημάτων',
      'triple_criminal_appeal': 'Τριμελές Εφετείο Κακουργημάτων',
      'five_criminal_appeal': 'Πενταμελές Εφετείο',
      'mixed_court': 'Μικτό Ορκωτό Δικαστήριο',
      'mixed_jury': 'Μικτό Ορκωτό',
      'appeal_criminal': 'Εφετείο Κακουργημάτων',
      'supreme_criminal': 'Άρειος Πάγος (Ποινικό)'
    };
    return courtTypes[courtType] || courtType;
  };

  const getDocumentTypeName = (documentType) => {
    const documentTypes = {
      // Αστικά
      'lawsuit': 'Αγωγή (Τακτική Διαδικασία)', 
      'special_lawsuit': 'Αγωγή (Ειδική Διαδικασία)',
      'voluntary_app': 'Αίτηση Εκουσίας', 
      'interim_app': 'Αίτηση ασφαλιστικών μέτρων',
      'appeal_peace': 'Έφεση κατά απόφασης Ειρηνοδικείου', 
      'appeal_single_first': 'Έφεση κατά απόφασης Μονομελούς Πρωτοδικείου',
      'cassation': 'Αίτηση αναίρεσης', 
      'injunction': 'Διαταγή Πληρωμής', 
      'objection': 'Ανακοπή',
      'objection_dp': 'Ανακοπή κατά Διαταγής Πληρωμής',
      'objection_933': 'Ανακοπή κατά Εκτέλεσης (933)',
      'objection_default': 'Ανακοπή ερημοδικίας',
      // Διοικητικά 3.1
      'appeal': 'Έφεση',
      'appeal_lawsuit': 'Αγωγή (Διοικητικό)',
      'appeal_admin': 'Προσφυγή (Διοικητικό)',
      'admin_prosfygi': 'Προσφυγή',
      'admin_agogi': 'Αγωγή (Διοικητικό)',
      'admin_efesi': 'Έφεση (Διοικητικό)',
      // Διοικητικά 3.2
      'cancellation_request': 'Αίτηση ακύρωσης',
      'cassation_admin': 'Αίτηση αναίρεσης (ΣτΕ)',
      // Ποινικά
      'criminal_appeal': 'Έφεση (Ποινική)',
      'criminal_cassation': 'Αναίρεση (Ποινική)',
      // Ένδικα μέσα / ειδικές ανακοπές
      'revision': 'Αναψηλάφηση',
      'third_party_obj': 'Τριτανακοπή',
      'rent_objection': 'Ανακοπή κατά διαταγής απόδοσης μισθίου',
      'objection_954': 'Ανακοπή άρθρου 954 ΚΠολΔ',
      'small_claims': 'Αγωγή μικροδιαφορών'
    };
    return documentTypes[documentType] || documentType;
  };

  const getCaseDescriptionLabel = (entryToCheck) => {
    if (entryToCheck.case_description_type === 'other' && entryToCheck.case_description_custom) {
      return entryToCheck.case_description_custom;
    }
    if (entryToCheck.case_description_type) {
      return CASE_DESCRIPTION_LABELS[entryToCheck.case_description_type] || entryToCheck.case_description_type;
    }
    return null;
  };

  const getProcedureType = () => {
    if (caseData && caseData.procedure_type) return caseData.procedure_type;
    return 'civil';
  };

  const getNewEntryOptions = () => {
    const procedureType = getProcedureType();
    switch (procedureType) {
      case 'criminal': return CRIMINAL_OTHER_OPTIONS;
      case 'administrative': return ADMINISTRATIVE_OTHER_OPTIONS;
      default: return CIVIL_OTHER_OPTIONS;
    }
  };

  const handleCreateNewEntry = async () => {
    if (!newEntryType && !customEntryTitle) {
      toast.error('Επιλέξτε τύπο εγγραφής ή συμπληρώστε χειροκίνητα');
      return;
    }
    setIsCreatingEntry(true);
    try {
      let title = '';
      if (newEntryType === 'custom') {
        title = customEntryTitle;
      } else {
        const options = getNewEntryOptions();
        const selectedOption = options.find(o => o.value === newEntryType);
        title = selectedOption ? selectedOption.label : newEntryType;
      }
      const newEntry = {
        entry_type: 'court_action', client_id: entry.client_id, case_id: entry.case_id,
        title: title, date: new Date().toISOString().split('T')[0], priority: 'medium', notes: '',
        opponent_details: entry.opponent_details || null, case_description_type: entry.case_description_type || null,
        case_description_custom: entry.case_description_custom || null
      };
      const response = await axios.post(`${API}/entries`, newEntry);
      toast.success('Η εγγραφή δημιουργήθηκε επιτυχώς');
      setNewEntryType(''); setCustomEntryTitle('');
      // Add new entry to local state immediately
      if (response.data) {
        setLocalEntries(prev => [...prev, response.data]);
      }
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error creating entry:', error);
      toast.error('Σφάλμα κατά τη δημιουργία εγγραφής');
    } finally {
      setIsCreatingEntry(false);
    }
  };

  // State για τις δυνητικές προθεσμίες υπολογισμένες από το backend (με αργίες/αναστολές).
  // Cached based on baseDate, document_type, procedure_type.
  const [potentialDeadlines, setPotentialDeadlines] = useState([]);

  // Templates ανά document_type — με πλήρη μεταδεδομένα για ακριβή backend υπολογισμό
  // (διάρκεια, τύπος ημερών, κατεύθυνση, αναστολή Αυγούστου)
  const civilDeadlineTemplates = {
    'lawsuit': [
      { id: 'epidosi_agogis',            title: 'Επίδοση αγωγής',
        duration: 30, type: 'calendar_days', direction: 'before', exclude_august: true },
      { id: 'katathesi_protaseon',       title: 'Κατάθεση προτάσεων',
        duration: 20, type: 'calendar_days', direction: 'before', exclude_august: true },
      { id: 'prosthiki_antikrousi',      title: 'Προσθήκη/αντίκρουση',
        duration: 5,  type: 'calendar_days', direction: 'before', exclude_august: true },
      { id: 'antagogi_paremvasi',        title: 'Ανταγωγή/παρέμβαση/προσεπίκληση',
        duration: 60, type: 'calendar_days', direction: 'before', exclude_august: true },
      { id: 'paremvasi_meta_anakoinosi', title: 'Παρέμβαση μετά από ανακοίνωση',
        duration: 90, type: 'calendar_days', direction: 'before', exclude_august: true },
    ],
    'special_lawsuit': [
      { id: 'epidosi_agogis_eidiki',         title: 'Επίδοση αγωγής',
        duration: 30, type: 'calendar_days', direction: 'before', exclude_august: true },
      { id: 'katathesi_protaseon_eidiki',    title: 'Κατάθεση προτάσεων',
        duration: 0,  type: 'calendar_days', direction: 'before', exclude_august: false },
      { id: 'prosthiki_eidiki',              title: 'Προσθήκη',
        duration: 5,  type: 'calendar_days', direction: 'before', exclude_august: false },
    ],
    'voluntary_app': [
      { id: 'epidosi_ekousia',              title: 'Επίδοση',
        duration: 20, type: 'calendar_days', direction: 'before', exclude_august: true },
      { id: 'katathesi_protaseon_ekousia',  title: 'Κατάθεση προτάσεων',
        duration: 0,  type: 'calendar_days', direction: 'before', exclude_august: false },
    ],
  };

  const normalizeTitle = (str) => str.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zα-ω]/g, '');

  // Recompute potential deadlines from backend whenever inputs change.
  // Χρησιμοποιεί τον πραγματικό DeadlineCalculator του backend → εφαρμόζει αργίες,
  // αναστολή Αυγούστου, αναστολή δικαστικών διακοπών (όταν Δημόσιο), παρέκταση.
  useEffect(() => {
    let cancelled = false;

    const compute = async () => {
      if (!caseData || !entry) { setPotentialDeadlines([]); return; }
      const procedureType = caseData.procedure_type;
      const documentType  = caseData.document_type;

      // Καμία δυνητική για διοικητικές/ποινικές
      if (procedureType !== 'civil') { setPotentialDeadlines([]); return; }
      const templates = civilDeadlineTemplates[documentType] || [];
      if (templates.length === 0) { setPotentialDeadlines([]); return; }

      // Εντοπισμός νεότερης ενεργής δικασίμου
      const activeHearings = (localEntries || [])
        .filter(e => isHearingEntry(e) && e.status !== 'ΑΝΤΙΚΑΤΑΣΤΑΘΗΚΕ' && e.date)
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      const latestHearingDate = activeHearings.length > 0 ? activeHearings[0].date : null;
      const baseDate = latestHearingDate || entry.filing_date || entry.created_at;
      if (!baseDate) { setPotentialDeadlines([]); return; }

      // Φίλτρο όσων έχουν ήδη εισαχθεί (loose match στους τίτλους)
      const existingNorm = (localEntries || []).map(e => normalizeTitle(e.title));
      const remaining = templates.filter(t => !existingNorm.some(existing =>
        normalizeTitle(t.title).includes(existing.substring(0, 10)) ||
        existing.includes(normalizeTitle(t.title).substring(0, 10))
      ));
      if (remaining.length === 0) { setPotentialDeadlines([]); return; }

      // Είναι Δημόσιο; (διοικητικά/ποινικά exit νωρίτερα οπότε εδώ false)
      const isPublic = !!caseData.is_public_entity;

      try {
        const res = await axios.post(`${API}/calculate-multiple-deadlines`, {
          base_date: new Date(baseDate).toISOString().split('T')[0],
          deadlines: remaining.map(t => ({
            name: t.title,
            duration: t.duration,
            type: t.type,
            direction: t.direction,
            is_public_entity: isPublic,
            exclude_august: t.exclude_august,
          })),
        }, { withCredentials: true });

        if (cancelled) return;

        const computed = (res.data?.deadlines || []).map((d, idx) => ({
          id: `potential_${remaining[idx].id}`,
          title: remaining[idx].title,
          date: d.deadline_date,
          status: 'ΜΗ ΕΠΙΛΕΓΜΕΝΗ',
          isPotential: true,
          deadlineId: remaining[idx].id,
          recalcBase: latestHearingDate ? 'νέα δικάσιμος' : 'αρχική κατάθεση',
        }));
        setPotentialDeadlines(computed);
      } catch (err) {
        // Fallback: απλός υπολογισμός calendar days (όπως πριν), σε περίπτωση
        // network/server error — να μη χάνεται η εμφάνιση δυνητικών.
        if (cancelled) return;
        const fallback = remaining.map(t => ({
          id: `potential_${t.id}`,
          title: t.title,
          date: new Date(new Date(baseDate).getTime() + t.duration * 86400000)
                  .toISOString().split('T')[0],
          status: 'ΜΗ ΕΠΙΛΕΓΜΕΝΗ',
          isPotential: true,
          deadlineId: t.id,
          recalcBase: latestHearingDate ? 'νέα δικάσιμος' : 'αρχική κατάθεση',
        }));
        setPotentialDeadlines(fallback);
      }
    };

    compute();
    return () => { cancelled = true; };
  }, [caseData?.document_type, caseData?.procedure_type, caseData?.is_public_entity,
      entry?.filing_date, entry?.created_at, localEntries]);

  // Επιστρέφει το state — διατήρηση παλιού API name για συμβατότητα.
  const getPotentialDeadlines = () => potentialDeadlines;

  const getAllEntries = () => {
    const existingEntries = localEntries && localEntries.length > 0 ? [...localEntries] : [entry];
    const potentialDeadlines = getPotentialDeadlines();
    
    // Hide replaced entries (ΑΝΤΙΚΑΤΑΣΤΑΘΗΚΕ) and suspended-by-status entries except hearings and completed ones
    const visibleEntries = existingEntries.filter(e => {
      // Exception 1: Always show hearings
      if (e.entry_type === 'hearing' || (e.title && e.title.toUpperCase().includes('ΔΙΚΑΣΙΜ'))) return true;
      // Exception 2: Always show completed entries
      if (e.status === 'ΟΛΟΚΛΗΡΩΜΕΝΗ') return true;
      // Hide replaced entries
      if (e.status === 'ΑΝΤΙΚΑΤΑΣΤΑΘΗΚΕ') return false;
      // Hide entries suspended due to a hearing status (ΠΑΡΑΙΤΗΣΗ/ΜΑΤΑΙΩΣΗ)
      if (e.suspended_by_status) return false;
      return true;
    });
    
    // Remove duplicates: potential deadlines that match an existing entry title
    const normalizeForDedup = (str) => {
      if (!str) return '';
      return str.toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zα-ω]/g, '');
    };
    
    // Collect all titles from existing (real) entries
    const existingTitles = new Set(visibleEntries.map(e => normalizeForDedup(e.title)));
    
    // Filter potential deadlines - only keep those not already in existing entries
    const filteredPotentials = potentialDeadlines.filter(p => {
      const normTitle = normalizeForDedup(p.title);
      return !existingTitles.has(normTitle);
    });
    
    const allEntries = [...visibleEntries, ...filteredPotentials];
    
    // Sort by date chronologically
    return allEntries.sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(a.date) - new Date(b.date);
    });
  };

  const allEntries = getAllEntries();

  // Check if entry is expired
  const isExpired = (entryItem) => {
    if (!entryItem.date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const entryDate = new Date(entryItem.date);
    entryDate.setHours(0, 0, 0, 0);
    return entryDate < today && 
           entryItem.status !== 'ΟΛΟΚΛΗΡΩΜΕΝΗ' && 
           entryItem.status !== 'ΛΗΓΜΕΝΗ' &&
           entryItem.status !== 'ΑΝΑΒΟΛΗ' &&
           entryItem.status !== 'ΜΑΤΑΙΩΣΗ' &&
           entryItem.status !== 'ΠΑΡΑΙΤΗΣΗ ΑΠΟ ΔΙΚΟΓΡΑΦΟ';
  };

  // Handle activating a potential (non-selected) entry
  const handleActivatePotentialEntry = async (potentialEntry) => {
    try {
      // Check if the date is in the past (expired)
      const entryDate = new Date(potentialEntry.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      entryDate.setHours(0, 0, 0, 0);
      const isEntryExpired = entryDate < today;
      
      // Get opponent_details and case_description_type from existing entries or entry prop
      const existingEntry = localEntries.find(e => !e.isPotential && e.opponent_details);
      const opponentDetails = existingEntry?.opponent_details || entry?.opponent_details || '';
      const caseDescType = existingEntry?.case_description_type || entry?.case_description_type || '';
      const caseDescCustom = existingEntry?.case_description_custom || entry?.case_description_custom || '';
      
      const newEntry = {
        entry_type: 'court_action',
        client_id: client?.id,
        case_id: caseData?.id,
        title: potentialEntry.title,
        date: potentialEntry.date,
        priority: 'medium',
        status: isEntryExpired ? 'ΛΗΓΜΕΝΗ' : 'ΣΕ ΑΝΑΜΟΝΗ',
        opponent_details: opponentDetails,
        case_description_type: caseDescType,
        case_description_custom: caseDescCustom
      };
      
      const response = await axios.post(`${API}/entries`, newEntry);
      toast.success('Η εγγραφή ενεργοποιήθηκε');
      setLocalEntries(prev => [...prev, response.data]);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error activating entry:', error);
      toast.error('Σφάλμα κατά την ενεργοποίηση');
    }
  };

  // Handle delete entry - opens confirmation dialog
  const handleDeleteClick = (entryId) => {
    setDeleteEntryId(entryId);
  };

  // Confirm delete entry
  const handleConfirmDelete = async () => {
    if (!deleteEntryId) return;
    try {
      await axios.delete(`${API}/entries/${deleteEntryId}`);
      toast.success('Η εγγραφή διαγράφηκε');
      // Update local state immediately
      setLocalEntries(prev => prev.filter(e => e.id !== deleteEntryId));
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Σφάλμα κατά τη διαγραφή');
    } finally {
      setDeleteEntryId(null);
    }
  };

  // Handle delete entire case (case + all entries)
  const handleDeleteCase = async () => {
    setIsDeletingCase(true);
    try {
      // Delete all entries for this case
      const deletePromises = localEntries
        .filter(e => !e.isPotential)
        .map(e => axios.delete(`${API}/entries/${e.id}`));
      
      await Promise.all(deletePromises);
      
      // Delete the case itself if it exists
      if (caseData?.id) {
        await axios.delete(`${API}/cases/${caseData.id}`);
      }
      
      toast.success('Η υπόθεση διαγράφηκε επιτυχώς');
      setDeleteCaseConfirm(false);
      onClose();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting case:', error);
      toast.error('Σφάλμα κατά τη διαγραφή της υπόθεσης');
    } finally {
      setIsDeletingCase(false);
    }
  };


  const renderStatusButton = (entryItem) => {
    const expired = isExpired(entryItem);
    return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpenStatusDropdown(openStatusDropdown === entryItem.id ? null : entryItem.id); }}
        disabled={updatingStatus === entryItem.id}
        className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-all shadow-sm ${
          expired ? 'bg-rose-600 text-white cursor-default' : 'bg-[#D97757] text-white hover:bg-[#C15F3C]'
        }`}
      >
        {updatingStatus === entryItem.id ? <span className="animate-pulse">...</span> : <><span>{getStatusLabel(entryItem)}</span><ChevronDown className="w-3 h-3" /></>}
      </button>
      {openStatusDropdown === entryItem.id && (
        <div className="absolute right-0 mt-1 w-52 bg-white border border-stone-200 rounded-md shadow-lg z-[100]">
          {getStatusOptions(entryItem).map((option) => (
            <button key={option.value} onClick={(e) => { e.stopPropagation(); handleStatusChange(entryItem.id, option.value); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-stone-100 first:rounded-t-md last:rounded-b-md ${(entryItem.status || 'ΣΕ ΑΝΑΜΟΝΗ') === option.value ? 'bg-slate-200 font-medium text-slate-900' : 'text-slate-700'}`}
            >{option.label}</button>
          ))}
        </div>
      )}
    </div>
  )};

  return (
    <Dialog open={!!entry} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto sm:rounded-2xl">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-base sm:text-xl font-semibold text-slate-900 text-left flex items-center gap-2.5">
            <span className="hidden sm:flex items-center justify-center w-8 h-8 rounded-lg bg-red-700/10 text-red-700 shrink-0">
              <FolderOpen className="w-4 h-4 sm:w-5 sm:h-5" />
            </span>
            Φάκελος υπόθεσης
          </DialogTitle>
        </DialogHeader>

        {canAssign && (
          <div data-testid="entry-assign-panel" className="flex flex-wrap items-center gap-2 border border-stone-200 rounded-xl px-3 py-2 bg-stone-50/60">
            <span className="text-xs font-semibold text-slate-600">Χρεώθηκε σε:</span>
            <Select value={entry?.assigned_to || 'none'} onValueChange={(v) => assignEntry(v === 'none' ? null : v)}>
              <SelectTrigger data-testid="entry-assign-select" className="h-8 w-auto gap-1 px-2 border-stone-300 rounded-md bg-white text-xs focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none" className="text-xs">— Χωρίς ανάθεση —</SelectItem>
                {assignableMembers.map((m) => (
                  <SelectItem key={m.id} value={m.id} className="text-xs">{m.name || m.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {caseData?.id && (
              <button
                data-testid="case-assign-btn"
                onClick={() => assignWholeCase(entry?.assigned_to || null)}
                disabled={!entry?.assigned_to}
                title="Χρέωση όλων των εγγραφών της υπόθεσης στον ίδιο δικηγόρο"
                className="text-xs font-medium px-2.5 h-8 rounded-md border border-stone-300 text-slate-600 hover:bg-white disabled:opacity-50"
              >
                Χρέωση όλης της υπόθεσης
              </button>
            )}
          </div>
        )}

        <div className="space-y-6 mt-4">
          {/* SECTION 1: Στοιχεία υπόθεσης */}
          <div className="bg-stone-50/80 border border-stone-200 rounded-xl p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="flex items-center gap-2.5 font-semibold text-sm sm:text-base tracking-tight text-slate-900">
                Στοιχεία υπόθεσης
              </h3>
              <div className="flex flex-col items-end gap-2">
                <button
                  onClick={handleEditCase}
                  className="text-xs text-slate-500 hover:text-red-700 transition-colors"
                >
                  Επεξεργασία
                </button>
                <button
                  onClick={() => setDeleteCaseConfirm(true)}
                  className="text-slate-400 hover:text-rose-600 transition-colors"
                  title="Διαγραφή υπόθεσης"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="space-y-3">
              {client && (
                <div className="bg-white p-3 rounded border border-stone-200">
                  <p className="text-sm font-medium text-slate-600 mb-1 flex items-center gap-1">
                    {client.client_type === 'natural' ? <User className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                    Πελάτης
                  </p>
                  <p className="text-base font-medium text-slate-900">{getClientName()}</p>
                </div>
              )}
              {getCaseDescriptionLabel(entry) && (
                <div className="bg-white p-3 rounded border border-stone-200">
                  <p className="text-sm font-medium text-slate-600 mb-1">Περιγραφή υπόθεσης</p>
                  <p className="text-base font-medium text-slate-900">{getCaseDescriptionLabel(entry)}</p>
                </div>
              )}
              {entry.opponent_details && (
                <div className="bg-white p-3 rounded border border-stone-200">
                  <p className="text-sm font-medium text-slate-600 mb-1">Στοιχεία αντιδίκου</p>
                  <p className="text-base font-medium text-slate-900">{entry.opponent_details}</p>
                </div>
              )}
              {caseData && caseData.gak && (
                <div className="bg-white p-3 rounded border border-stone-200">
                  <p className="text-sm font-medium text-slate-600 mb-1">Αριθμός κατάθεσης (ΓΑΚ)</p>
                  <p className="text-base font-medium text-slate-900">{caseData.gak}</p>
                </div>
              )}
              {caseData && caseData.court_type && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-white p-3 rounded border border-stone-200">
                    <p className="text-sm font-medium text-slate-600 mb-1">Δικαστήριο</p>
                    <p className="text-base font-medium text-slate-900">{getCourtTypeName(caseData.court_type)}</p>
                  </div>
                  {caseData.court_location && (
                    <div className="bg-white p-3 rounded border border-stone-200">
                      <p className="text-sm font-medium text-slate-600 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" />Έδρα</p>
                      <p className="text-base font-medium text-slate-900">{caseData.court_location}</p>
                    </div>
                  )}
                </div>
              )}
              {caseData && caseData.document_type && (
                <div className="bg-white p-3 rounded border border-stone-200">
                  <p className="text-sm font-medium text-slate-600 mb-1">Είδος δικογράφου</p>
                  <p className="text-base font-medium text-slate-900">{getDocumentTypeName(caseData.document_type)}</p>
                </div>
              )}
              {caseData && caseData.client_role && (
                <div className="bg-white p-3 rounded border border-stone-200" data-testid="case-client-role">
                  <p className="text-sm font-medium text-slate-600 mb-1">Ιδιότητα εντολέα</p>
                  <p className="text-base font-medium text-slate-900">{caseData.client_role}</p>
                </div>
              )}
            </div>
          </div>

          {/* Post-it σημειώσεις φακέλου */}
          {noteCaseId && (
          <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-3 sm:p-4" data-testid="case-postit-section">
            <h3 className="flex items-center gap-2.5 font-semibold text-sm sm:text-base tracking-tight text-slate-900 mb-3">
              <StickyNote className="w-4 h-4 text-amber-600" />
              Σημειώσεις (post-it)
            </h3>
            {caseNotes.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-3">
                {caseNotes.map((n, idx) => (
                  <div
                    key={n.id}
                    data-testid={`case-note-${n.id}`}
                    className={`relative bg-yellow-200 border border-yellow-300 rounded-sm shadow-sm p-3 pt-4 ${idx % 2 === 0 ? 'rotate-1' : '-rotate-1'}`}
                  >
                    <button
                      data-testid={`case-note-delete-${n.id}`}
                      onClick={() => deleteCaseNote(n.id)}
                      title="Διαγραφή σημείωσης"
                      className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center text-yellow-700/60 hover:text-red-700"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    {editingNoteId === n.id ? (
                      <textarea
                        data-testid={`case-note-edit-input-${n.id}`}
                        value={editingNoteText}
                        onChange={(e) => setEditingNoteText(e.target.value)}
                        onBlur={() => saveNoteEdit(n.id)}
                        autoFocus
                        rows={3}
                        className="w-full bg-transparent text-sm text-slate-800 focus:outline-none resize-none"
                      />
                    ) : (
                      <p
                        data-testid={`case-note-text-${n.id}`}
                        onClick={() => { setEditingNoteId(n.id); setEditingNoteText(n.text); }}
                        title="Πατήστε για επεξεργασία"
                        className="text-sm text-slate-800 whitespace-pre-wrap break-words cursor-text"
                        style={{ lineHeight: 1.45 }}
                      >
                        {n.text}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <textarea
                data-testid="case-note-new-input"
                value={newNoteText}
                onChange={(e) => setNewNoteText(e.target.value)}
                placeholder="Νέα σημείωση..."
                rows={1}
                className="flex-1 min-h-[44px] px-3 py-2.5 border border-amber-300 rounded-lg bg-white text-sm focus:outline-none focus:border-amber-500 resize-none"
              />
              <button
                data-testid="case-note-add-btn"
                onClick={addCaseNote}
                disabled={!newNoteText.trim()}
                className="h-11 px-4 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold disabled:opacity-40 shrink-0"
              >
                Προσθήκη
              </button>
            </div>
          </div>
          )}

          {/* SECTION 2: Καταχώρηση νέας εγγραφής (κρυφή για προθεσμίες εργασίας) */}
          {!(entry?.entry_type === 'other_activity' && (entry?.title || '').toLowerCase().includes('προθεσμία εργασίας')) && (
          <div className="bg-stone-50/80 border border-stone-200 rounded-xl p-3 sm:p-4">
            <h3 className="flex items-center gap-2.5 font-semibold text-sm sm:text-base tracking-tight text-slate-900 mb-3">
              Καταχώρηση νέας εγγραφής
            </h3>
            <div className="space-y-3">
              <div>
                <Label htmlFor="new-entry-type">Επιλέξτε τύπο εγγραφής</Label>
                <Select value={newEntryType} onValueChange={(value) => { setNewEntryType(value); if (value !== 'custom') setCustomEntryTitle(''); }}>
                  <SelectTrigger className="h-11 bg-white border-stone-300"><SelectValue placeholder="Επιλέξτε..." /></SelectTrigger>
                  <SelectContent>
                    {getNewEntryOptions().map((option) => (<SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>))}
                    <SelectItem value="custom">Άλλη πράξη (χειροκίνητη καταχώρηση)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newEntryType === 'custom' && (
                <div>
                  <Label htmlFor="custom-entry-title">Τίτλος εγγραφής (κεφαλαία χωρίς τόνους)</Label>
                  <Input id="custom-entry-title" value={customEntryTitle} onChange={(e) => setCustomEntryTitle(toUpperCaseNoAccents(e.target.value))} placeholder="Π.χ. ΑΠΟΦΑΣΗ ΔΙΚΑΣΤΗΡΙΟΥ" className="h-11 bg-white border-stone-300 uppercase" />
                </div>
              )}
              {OTHER_ACTION_OPTIONS.includes(newEntryType) ? (
                <OtherActionForm
                  entry={entry}
                  optionValue={newEntryType}
                  procedureType={getProcedureType()}
                  onCreated={() => {
                    setNewEntryType('');
                    setCustomEntryTitle('');
                    if (onRefresh) onRefresh();
                  }}
                  onCancel={() => setNewEntryType('')}
                />
              ) : (
                <Button onClick={handleCreateNewEntry} disabled={isCreatingEntry || (!newEntryType && !customEntryTitle) || (newEntryType === 'custom' && !customEntryTitle)} className="w-full bg-red-700 hover:bg-red-800 text-white">
                  {isCreatingEntry ? 'Δημιουργία...' : 'Δημιουργία εγγραφής'}
                </Button>
              )}
            </div>
          </div>
          )}

          {/* SECTION 3: Εγγραφές */}
          <div className="bg-stone-50/80 border border-stone-200 rounded-xl p-3 sm:p-4">
            <h3 className="flex items-center gap-2.5 font-semibold text-sm sm:text-base tracking-tight text-slate-900 mb-3 sm:mb-4">
              Εγγραφές ({allEntries.filter(e => !e.isPotential).length})
            </h3>
            <div className="space-y-3 sm:space-y-4">
              {allEntries.map((entryItem) => {
                const isExpiredEntry = !entryItem.isPotential && isExpired(entryItem);
                const isHearing = isHearingEntry(entryItem);
                return (
                <div key={entryItem.id} className={`p-3 sm:p-4 rounded-lg border ${entryItem.isPotential ? 'bg-stone-50 border-dashed border-stone-300' : 'bg-white border-stone-200'}`}>
                  {/* Row 1: Title + Status on the same row (mobile & desktop) */}
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-sm sm:text-base font-bold flex-1 min-w-0 break-words ${entryItem.isPotential ? 'text-slate-500' : 'text-red-700'}`}>{capitalizeFirst(entryItem.title)}</p>
                    <div className="flex-shrink-0">
                      {entryItem.isPotential ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-slate-200 text-slate-500">
                          Μη επιλεγμένη
                        </span>
                      ) : isExpiredEntry ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-red-600 text-white">
                          Ληγμένη
                        </span>
                      ) : isHearing ? (
                        <div className="relative">
                          <button
                            onClick={(e) => { e.stopPropagation(); setOpenStatusDropdown(openStatusDropdown === `hearing_${entryItem.id}` ? null : `hearing_${entryItem.id}`); }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-all shadow-sm bg-[#D97757] text-white hover:bg-[#C15F3C]"
                          >
                            <span>{
                              entryItem.status === 'ΣΥΖΗΤΗΘΗΚΕ' ? 'Συζητήθηκε' :
                              entryItem.status === 'ΑΝΑΒΟΛΗ' ? 'Αναβολή' :
                              entryItem.status === 'ΜΑΤΑΙΩΣΗ' ? 'Ματαίωση' :
                              entryItem.status === 'ΠΑΡΑΙΤΗΣΗ ΑΠΟ ΔΙΚΟΓΡΑΦΟ' ? 'Παραίτηση' :
                              'Σε αναμονή'
                            }</span>
                            <ChevronDown className="w-3 h-3" />
                          </button>
                          {openStatusDropdown === `hearing_${entryItem.id}` && (
                            <div className="absolute right-0 mt-1 w-52 bg-white border border-stone-200 rounded-md shadow-lg z-[100]">
                              {HEARING_STATUS_OPTIONS.map((option) => (
                                <button 
                                  key={option.value} 
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    handleUpdateHearingStatus(entryItem.id, option.value);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-stone-100 first:rounded-t-md last:rounded-b-md ${
                                    (entryItem.status || 'ΣΕ ΑΝΑΜΟΝΗ') === option.value ? 'bg-slate-200 font-medium text-slate-900' : 'text-slate-700'
                                  }`}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        renderStatusButton(entryItem)
                      )}
                    </div>
                  </div>
                  {/* Row 2: Date + Delete/Activate button — tighter to row 1 */}
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className={`text-xs sm:text-sm flex-1 min-w-0 truncate ${entryItem.isPotential ? 'text-slate-400' : 'text-slate-600'}`}>
                      <span className="font-medium">
                        {entryItem.status === 'ΣΥΖΗΤΗΘΗΚΕ' ? 'Συζήτηση:' : (isHearingEntry(entryItem) || entryItem.entry_type === 'other_activity') ? 'Ημερομηνία:' : 'Λήξη προθεσμίας:'}
                      </span> {formatDate(entryItem.date)}
                      {isHearingEntry(entryItem) && !entryItem.isPotential && (
                        <span className="inline-flex items-center gap-1 ml-2 align-middle">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          <input
                            type="time"
                            data-testid={`hearing-time-input-${entryItem.id}`}
                            title="Ώρα δικασίμου"
                            value={timeDrafts[entryItem.id] ?? getEntryTime(entryItem)}
                            onChange={(ev) => setTimeDrafts((prev) => ({ ...prev, [entryItem.id]: ev.target.value }))}
                            onBlur={(ev) => { saveHearingTime(entryItem, ev.target.value); setTimeDrafts((prev) => { const n = { ...prev }; delete n[entryItem.id]; return n; }); }}
                            onKeyDown={(ev) => { if (ev.key === 'Enter') ev.target.blur(); }}
                            onClick={(ev) => ev.stopPropagation()}
                            className="h-6 px-1 border border-stone-300 rounded text-xs bg-white text-slate-700 focus:outline-none focus:border-stone-500"
                          />
                        </span>
                      )}
                    </p>
                    {!entryItem.isPotential && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(entryItem.id); }}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                        title="Διαγραφή εγγραφής"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {entryItem.isPotential && (
                      <button
                        onClick={() => handleActivatePotentialEntry(entryItem)}
                        className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors flex-shrink-0"
                        title="Ενεργοποίηση εγγραφής"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Description (notes / case_description_custom) - show full for other_activity & any entry with notes */}
                  {!entryItem.isPotential && (entryItem.case_description_custom || entryItem.notes) && (
                    <p className="mt-0.5 text-xs sm:text-sm text-slate-600 whitespace-pre-wrap break-words">
                      {entryItem.case_description_custom || entryItem.notes}
                    </p>
                  )}

                  {/* Row 3: Ημέρες που απομένουν (only for active non-potential entries) */}
                  {!entryItem.isPotential && !isExpiredEntry && entryItem.status !== 'ΟΛΟΚΛΗΡΩΜΕΝΗ' && entryItem.status !== 'ΣΥΖΗΤΗΘΗΚΕ' && (() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const target = new Date(entryItem.date);
                    target.setHours(0, 0, 0, 0);
                    const diffDays = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
                    let label = '';
                    if (diffDays === 0) label = 'Σήμερα';
                    else if (diffDays === 1) label = 'Αύριο';
                    else if (diffDays < 0) label = `${Math.abs(diffDays)} ημέρες πριν`;
                    else label = `Απομένουν ${diffDays} ημέρες`;
                    const colorClass = diffDays < 0
                      ? 'text-rose-600'
                      : diffDays < 10
                        ? 'text-red-600'
                        : 'text-[#D97757]';
                    return (
                      <p className={`mt-2 text-sm font-semibold ${colorClass}`}>
                        {label}
                      </p>
                    );
                  })()}
                  {entryItem.status === 'ΟΛΟΚΛΗΡΩΜΕΝΗ' && (
                    <p className="mt-2 text-sm font-semibold text-green-600">Ολοκληρώθηκε</p>
                  )}
                  {pendingScheduleEntryId === entryItem.id && (
                    <div className="flex justify-center my-3" onClick={(e) => e.stopPropagation()}>
                      <div 
                        ref={datePickerRef}
                        className="bg-white border border-stone-200 rounded-lg shadow-xl p-3"
                      >
                        <DatePickerWithInput
                          selected={scheduledDateTemp}
                          onChange={(date) => handleScheduledDateSelect(entryItem.id, date)}
                          minDate={new Date()}
                        />
                        <button
                          onClick={() => { setPendingScheduleEntryId(null); setScheduledDateTemp(null); }}
                          className="w-full text-xs text-slate-500 hover:text-red-600 py-1 border-t border-stone-100 mt-2"
                        >
                          Ακύρωση
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Inline Date Picker για Αναβολή δικασίμου */}
                  {postponementEntryId === entryItem.id && (
                    <div className="flex justify-center my-3" onClick={(e) => e.stopPropagation()}>
                      <div className="bg-white border border-amber-300 rounded-lg shadow-xl p-3">
                        <p className="text-sm font-medium text-red-700 mb-2 text-center">Επιλέξτε νέα ημερομηνία δικασίμου</p>
                        <DatePickerWithInput
                          selected={postponementDate}
                          onChange={(date) => handlePostponementDateSelect(date)}
                          minDate={new Date()}
                        />
                        <button
                          onClick={() => { setPostponementEntryId(null); setPostponementDate(null); }}
                          className="w-full text-xs text-slate-500 hover:text-red-600 py-1 border-t border-stone-100 mt-2"
                        >
                          Ακύρωση
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          </div>
          
          {/* Εργασίες που σχετίζονται με την υπόθεση */}
          {caseTasks.length > 0 && (
            <div className="bg-stone-50/80 border border-stone-200 rounded-xl p-3 sm:p-4" data-testid="case-tasks-section">
              <h3 className="flex items-center gap-2.5 font-semibold text-sm sm:text-base tracking-tight text-slate-900 mb-3">
                Εργασίες υπόθεσης ({caseTasks.length})
              </h3>
              <div className="space-y-1.5">
                {caseTasks.map((t) => (
                  <div key={t.id} data-testid={`case-task-${t.id}`}
                    className="flex items-center justify-between gap-2 bg-white border border-stone-200 rounded-lg px-2.5 py-1.5">
                    <p className={`text-xs sm:text-sm min-w-0 truncate ${t.status === 'done' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                      <span className="text-slate-400 mr-1.5">
                        {new Date(`${t.date}T12:00:00`).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        {t.start_time ? ` · ${t.start_time}` : ''}
                      </span>
                      {t.title}
                    </p>
                    <span className={`text-[11px] font-semibold shrink-0 ${t.status === 'done' ? 'text-green-600' : 'text-amber-600'}`}>
                      {t.status === 'done' ? 'Ολοκληρώθηκε' : 'Εκκρεμεί'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SECTION 4: Οικονομικά στοιχεία Υπόθεσης */}
          <div className="bg-stone-50/80 border border-stone-200 rounded-xl p-3 sm:p-4 mt-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3 sm:mb-4">
              <h3 className="font-semibold text-sm sm:text-base tracking-tight text-slate-900 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 sm:w-5 sm:h-5 text-red-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                  <line x1="1" y1="10" x2="23" y2="10"></line>
                </svg>
                Οικονομικά στοιχεία
              </h3>
              
              {/* Balance Display */}
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white px-2 sm:px-4 py-1 sm:py-2 rounded-lg border border-stone-300 self-start sm:self-auto">
                <span className="text-xs sm:text-sm font-medium text-slate-600">Υπόλοιπο:</span>
                <span className={`text-sm sm:text-base font-bold ${
                  (() => {
                    const fees = financialEntries.filter(f => f.entry_type === 'fee').reduce((sum, f) => sum + (f.amount || 0), 0);
                    const expenses = financialEntries.filter(f => f.entry_type === 'expense').reduce((sum, f) => sum + (f.amount || 0), 0);
                    const payments = financialEntries.filter(f => f.entry_type === 'payment').reduce((sum, f) => sum + (f.amount || 0), 0);
                    const balance = fees + expenses - payments;
                    return balance >= 0 ? 'text-green-600' : 'text-red-600';
                  })()
                }`}>
                  {(() => {
                    const fees = financialEntries.filter(f => f.entry_type === 'fee').reduce((sum, f) => sum + (f.amount || 0), 0);
                    const expenses = financialEntries.filter(f => f.entry_type === 'expense').reduce((sum, f) => sum + (f.amount || 0), 0);
                    const payments = financialEntries.filter(f => f.entry_type === 'payment').reduce((sum, f) => sum + (f.amount || 0), 0);
                    const balance = fees + expenses - payments;
                    return formatCurrency(balance);
                  })()}
                </span>
              </div>
            </div>
            
            {/* Quick Add Buttons */}
            <div className="flex gap-2 mb-3 sm:mb-4">
              <button
                onClick={() => setShowFinancialForm('fee')}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 bg-red-700 hover:bg-red-800 text-white text-sm font-semibold rounded-lg transition-all shadow-sm"
                data-testid="add-fee-btn"
              >
                <Plus className="w-4 h-4" strokeWidth={2.5} />
                Νέα χρέωση
              </button>
              <button
                onClick={() => setShowFinancialForm('payment')}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 border-2 border-red-700 text-red-700 hover:bg-red-50 text-sm font-semibold rounded-lg transition-all"
                data-testid="add-payment-btn"
              >
                <Plus className="w-4 h-4" strokeWidth={2.5} />
                Νέα πληρωμή
              </button>
            </div>
            
            {/* Quick Add Form */}
            {showFinancialForm && (
              <div className="bg-white border border-stone-200 rounded-xl p-4 mb-4">
                <h4 className="flex items-center gap-2.5 font-medium text-slate-900 mb-3">
                  {showFinancialForm === 'fee' ? 'Νέα χρέωση' : 'Νέα πληρωμή'}
                </h4>
                <div className="space-y-3">
                  {/* Sub-toggle: Αμοιβή / Έξοδο */}
                  {showFinancialForm === 'fee' && (
                    <div>
                      <Label className="text-sm">Τύπος χρέωσης</Label>
                      <div className="flex gap-2 mt-1">
                        <button
                          type="button"
                          onClick={() => setFinancialFormData(f => ({ ...f, entry_type: 'fee' }))}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 sm:py-1.5 text-sm rounded-lg border transition-all duration-200 ${
                            financialFormData.entry_type === 'fee'
                              ? 'bg-red-700 text-white border-red-700 shadow-md shadow-red-900/25'
                              : 'bg-white border-stone-300 hover:border-stone-400 hover:bg-stone-50 text-slate-700'
                          }`}
                        >
                          Αμοιβή
                        </button>
                        <button
                          type="button"
                          onClick={() => setFinancialFormData(f => ({ ...f, entry_type: 'expense' }))}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 sm:py-1.5 text-sm rounded-lg border transition-all duration-200 ${
                            financialFormData.entry_type === 'expense'
                              ? 'bg-red-700 text-white border-red-700 shadow-md shadow-red-900/25'
                              : 'bg-white border-stone-300 hover:border-stone-400 hover:bg-stone-50 text-slate-700'
                          }`}
                        >
                          Έξοδο
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Description (moved to top) */}
                  <div>
                    <Label className="text-sm">Περιγραφή</Label>
                    <Input
                      placeholder="π.χ. Αμοιβή για αγωγή"
                      value={financialFormData.description}
                      onChange={(e) => setFinancialFormData(f => ({ ...f, description: e.target.value }))}
                      className="mt-1 h-9 text-sm placeholder:text-sm"
                    />
                  </div>

                  {/* Fee Type (only when fee selected) */}
                  {showFinancialForm === 'fee' && financialFormData.entry_type === 'fee' && (
                    <div>
                      <Label className="text-sm">Τρόπος αμοιβής</Label>
                      <div className="flex gap-2 mt-1">
                        <button
                          type="button"
                          onClick={() => setFinancialFormData(f => ({ ...f, fee_type: 'flat' }))}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 sm:py-1.5 text-sm rounded-lg border transition-all duration-200 ${
                            financialFormData.fee_type === 'flat'
                              ? 'bg-red-700 text-white border-red-700 shadow-md shadow-red-900/25'
                              : 'bg-white border-stone-300 hover:border-stone-400 hover:bg-stone-50 text-slate-700'
                          }`}
                        >
                          Αμοιβή
                        </button>
                        <button
                          type="button"
                          onClick={() => setFinancialFormData(f => ({ ...f, fee_type: 'hourly' }))}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 sm:py-1.5 text-sm rounded-lg border transition-all duration-200 ${
                            financialFormData.fee_type === 'hourly'
                              ? 'bg-red-700 text-white border-red-700 shadow-md shadow-red-900/25'
                              : 'bg-white border-stone-300 hover:border-stone-400 hover:bg-stone-50 text-slate-700'
                          }`}
                        >
                          Χρονοχρέωση
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Χρονοχρέωση layout: Ώρες × €/ώρα, Ποσό (read-only), Date */}
                  {showFinancialForm === 'fee' && financialFormData.entry_type === 'fee' && financialFormData.fee_type === 'hourly' ? (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm">Ώρες</Label>
                          <Input
                            type="number"
                            step="0.5"
                            placeholder="π.χ. 2.5"
                            value={financialFormData.hours}
                            onChange={(e) => setFinancialFormData(f => ({ ...f, hours: e.target.value }))}
                            className="mt-1 h-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Αμοιβή / ώρα (€)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="π.χ. 80"
                            value={financialFormData.hourly_rate}
                            onChange={(e) => setFinancialFormData(f => ({ ...f, hourly_rate: e.target.value }))}
                            className="mt-1 h-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm">Ποσό (€)</Label>
                        <Input
                          readOnly
                          value={(() => {
                            const h = parseFloat(financialFormData.hours) || 0;
                            const r = parseFloat(financialFormData.hourly_rate) || 0;
                            return (h * r).toFixed(2);
                          })()}
                          className="mt-1 h-9 text-sm bg-stone-50 text-slate-700"
                        />
                      </div>
                    </>
                  ) : (
                    <div>
                      <Label className="text-sm">Ποσό (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={financialFormData.amount}
                        onChange={(e) => setFinancialFormData(f => ({ ...f, amount: e.target.value }))}
                        className="mt-1 h-10 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  )}

                  {/* Ημερομηνία (πάντα από κάτω, ξεχωριστή σειρά, καλεντάρι κεντραρισμένο) */}
                  <div>
                    <Label className="text-sm">Ημερομηνία</Label>
                    <div className="space-y-1 mt-1">
                      <input
                        type="text"
                        value={financialFormData.date ? new Date(financialFormData.date).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}
                        onChange={(e) => {
                          const parts = e.target.value.split('/');
                          if (parts.length === 3) {
                            const day = parseInt(parts[0], 10);
                            const month = parseInt(parts[1], 10);
                            const year = parseInt(parts[2], 10);
                            if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900) {
                              const d = new Date(year, month - 1, day);
                              if (!isNaN(d.getTime())) {
                                setFinancialFormData(f => ({ ...f, date: d.toISOString().split('T')[0] }));
                              }
                            }
                          }
                        }}
                        placeholder="ηη/μμ/εεεε"
                        className="w-full h-8 px-3 py-1 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-0 focus:border-stone-400 bg-white"
                        autoComplete="off"
                      />
                      <div className="flex justify-center">
                        <DatePicker
                          selected={financialFormData.date ? new Date(financialFormData.date) : new Date()}
                          onChange={(date) => setFinancialFormData(f => ({ ...f, date: date ? date.toISOString().split('T')[0] : '' }))}
                          locale="el"
                          dateFormat="dd/MM/yyyy"
                          inline
                          fixedHeight
                          calendarClassName="small-calendar"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowFinancialForm(null);
                        setFinancialFormData({ entry_type: 'fee', amount: '', description: '', date: new Date().toISOString().split('T')[0], fee_type: 'flat', hours: '', hourly_rate: '' });
                      }}
                      className="px-6 py-2.5 text-base"
                    >
                      Ακύρωση
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSaveFinancialEntry}
                      className="bg-red-700 hover:bg-red-800 px-6 py-2.5 text-base"
                    >
                      Αποθήκευση
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Entries List */}
            {financialEntries.length > 0 ? (
              <div className="space-y-3">
                {financialEntries.map((finEntry) => {
                  return (
                    <div key={finEntry.id} className="p-3 rounded-lg bg-white border border-stone-200">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-900 truncate">{finEntry.description}</p>
                          <div className="flex items-center justify-between mt-0.5">
                            <span className="text-xs text-slate-500">
                              {new Date(finEntry.date).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </span>
                            <span className={`font-bold text-xs ${
                              finEntry.entry_type === 'fee' ? 'text-red-700' :
                              finEntry.entry_type === 'expense' ? 'text-amber-700' :
                              'text-slate-700'
                            }`}>
                              {finEntry.entry_type === 'payment' ? '-' : ''}{formatCurrency(finEntry.amount)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Summary - Only Balance */}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">Δεν υπάρχουν οικονομικά στοιχεία για αυτή την υπόθεση</p>
            )}
          </div>

          {/* SECTION 5: Σχετικά αρχεία Υπόθεσης */}
          {entry?.case_id && (
            <div className="bg-stone-50/80 border border-stone-200 rounded-xl p-3 sm:p-4 mt-4" data-testid="case-documents-section">
              <CaseDocuments caseId={entry.case_id} />
            </div>
          )}
        </div>

        {/* Κλείσιμο button - aligned with right edge */}
        <div className="flex justify-end mt-4 pt-4 border-t border-stone-200">
          <Button onClick={onClose} variant="outline" className="rounded-lg px-6">Κλείσιμο</Button>
        </div>
      </DialogContent>

      {/* Edit Case Modal */}
      <Dialog open={isEditingCase} onOpenChange={(open) => {
        setIsEditingCase(open);
        if (!open) setChangeDateSelected(false);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto sm:rounded-2xl" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2.5">
              <span className="hidden sm:flex items-center justify-center w-8 h-8 rounded-lg bg-red-700/10 text-red-700 shrink-0">
                <Scale className="w-4 h-4 sm:w-5 sm:h-5" />
              </span>
              Επεξεργασία στοιχείων υπόθεσης
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* 1. Στοιχεία αντιδίκου */}
            <div>
              <Label htmlFor="edit-opponent" className="text-base font-medium">Στοιχεία αντιδίκου</Label>
              <Input
                id="edit-opponent"
                value={editCaseForm.opponent_details}
                onChange={(e) => setEditCaseForm({...editCaseForm, opponent_details: toUpperCaseNoAccents(e.target.value)})}
                className="h-12 bg-white border-stone-300 !text-base text-slate-900"
                autoComplete="off"
                onFocus={(e) => {
                  e.target.setSelectionRange(e.target.value.length, e.target.value.length);
                }}
              />
            </div>

            {/* 2. Είδος δικογράφου */}
            <div>
              <Label className="text-base font-medium">Είδος δικογράφου</Label>
              <Select 
                value={editCaseForm.document_type} 
                onValueChange={(value) => setEditCaseForm({...editCaseForm, document_type: value})}
              >
                <SelectTrigger className="h-12 bg-white border-stone-300 text-base">
                  <SelectValue placeholder="Επιλέξτε είδος δικογράφου" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lawsuit">Αγωγή (Τακτική Διαδικασία)</SelectItem>
                  <SelectItem value="special_lawsuit">Αγωγή (Ειδική Διαδικασία)</SelectItem>
                  <SelectItem value="voluntary_app">Αίτηση Εκουσίας</SelectItem>
                  <SelectItem value="interim_app">Αίτηση ασφαλιστικών μέτρων</SelectItem>
                  <SelectItem value="appeal_peace">Έφεση κατά απόφασης Ειρηνοδικείου</SelectItem>
                  <SelectItem value="appeal_single_first">Έφεση κατά απόφασης Μονομελούς Πρωτοδικείου</SelectItem>
                  <SelectItem value="cassation">Αίτηση αναίρεσης</SelectItem>
                  <SelectItem value="injunction">Διαταγή Πληρωμής</SelectItem>
                  <SelectItem value="objection">Ανακοπή</SelectItem>
                  <SelectItem value="objection_dp">Ανακοπή κατά Διαταγής Πληρωμής</SelectItem>
                  <SelectItem value="objection_933">Ανακοπή κατά Εκτέλεσης (933)</SelectItem>
                  <SelectItem value="objection_default">Ανακοπή ερημοδικίας</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 3. Δικαστήριο */}
            <div>
              <Label className="text-base font-medium">Δικαστήριο</Label>
              <Select 
                value={editCaseForm.court_type} 
                onValueChange={(value) => setEditCaseForm({...editCaseForm, court_type: value})}
              >
                <SelectTrigger className="h-12 bg-white border-stone-300 text-base">
                  <SelectValue placeholder="Επιλέξτε δικαστήριο" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single_peace">Μονομελές Ειρηνοδικείο</SelectItem>
                  <SelectItem value="multi_peace">Πολυμελές Ειρηνοδικείο</SelectItem>
                  <SelectItem value="single_first">Μονομελές Πρωτοδικείο</SelectItem>
                  <SelectItem value="multi_first">Πολυμελές Πρωτοδικείο</SelectItem>
                  <SelectItem value="single_appeal">Μονομελές Εφετείο</SelectItem>
                  <SelectItem value="multi_appeal">Τριμελές Εφετείο</SelectItem>
                  <SelectItem value="supreme">Άρειος Πάγος</SelectItem>
                  <SelectItem value="single_admin_first">Μονομελές Διοικητικό Πρωτοδικείο</SelectItem>
                  <SelectItem value="triple_admin_first">Τριμελές Διοικητικό Πρωτοδικείο</SelectItem>
                  <SelectItem value="administrative_first">Διοικητικό Πρωτοδικείο</SelectItem>
                  <SelectItem value="admin_appeal">Διοικητικό Εφετείο</SelectItem>
                  <SelectItem value="triple_admin_appeal">Τριμελές Διοικητικό Εφετείο</SelectItem>
                  <SelectItem value="administrative_appeal">Διοικητικό Εφετείο</SelectItem>
                  <SelectItem value="council_of_state">Συμβούλιο της Επικρατείας</SelectItem>
                  <SelectItem value="single_criminal">Μονομελές Πλημμελειοδικείο</SelectItem>
                  <SelectItem value="three_criminal">Τριμελές Πλημμελειοδικείο</SelectItem>
                  <SelectItem value="single_criminal_appeal">Μονομελές Εφετείο Κακουργημάτων</SelectItem>
                  <SelectItem value="triple_criminal_appeal">Τριμελές Εφετείο Κακουργημάτων</SelectItem>
                  <SelectItem value="five_criminal_appeal">Πενταμελές Εφετείο</SelectItem>
                  <SelectItem value="mixed_court">Μικτό Ορκωτό Δικαστήριο</SelectItem>
                  <SelectItem value="mixed_jury">Μικτό Ορκωτό</SelectItem>
                  <SelectItem value="appeal_criminal">Εφετείο Κακουργημάτων</SelectItem>
                  <SelectItem value="supreme_criminal">Άρειος Πάγος (Ποινικό)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 4. Έδρα */}
            <div>
              <Label className="text-base font-medium">Έδρα</Label>
              <Select 
                value={editCaseForm.court_location} 
                onValueChange={(value) => setEditCaseForm({...editCaseForm, court_location: value})}
              >
                <SelectTrigger className="h-12 bg-white border-stone-300 text-base">
                  <SelectValue placeholder="Επιλέξτε έδρα" />
                </SelectTrigger>
                <SelectContent>
                  {courtLocations.map(location => (
                    <SelectItem key={location} value={location}>{location}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 5. Περιγραφή υπόθεσης */}
            <div>
              <Label htmlFor="edit-case-desc" className="text-base font-medium">Περιγραφή υπόθεσης</Label>
              <Select 
                value={editCaseForm.case_description_type} 
                onValueChange={(value) => setEditCaseForm({...editCaseForm, case_description_type: value})}
              >
                <SelectTrigger className="h-12 bg-white border-stone-300 text-base">
                  <SelectValue placeholder="Επιλέξτε περιγραφή" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CASE_DESCRIPTION_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {editCaseForm.case_description_type === 'other' && (
              <div>
                <Label htmlFor="edit-case-custom" className="text-base font-medium">Προσαρμοσμένη περιγραφή</Label>
                <Input
                  id="edit-case-custom"
                  value={editCaseForm.case_description_custom}
                  onChange={(e) => setEditCaseForm({...editCaseForm, case_description_custom: e.target.value})}
                  className="h-12 bg-white border-stone-300 text-base"
                />
              </div>
            )}

            {caseData?.procedure_type === 'civil' && (
              <label className="flex items-center gap-2.5 cursor-pointer" data-testid="edit-mediation-toggle">
                <input
                  type="checkbox"
                  checked={editCaseForm.mediation_required === true}
                  onChange={(e) => setEditCaseForm({ ...editCaseForm, mediation_required: e.target.checked })}
                  className="w-4 h-4 accent-red-700"
                />
                <span className="text-sm font-medium text-slate-700">Απαιτείται διαμεσολάβηση</span>
              </label>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-stone-200">
              <Button variant="outline" onClick={() => { setIsEditingCase(false); setChangeDateSelected(false); }}>
                Ακύρωση
              </Button>
              <Button onClick={handleSaveCase} className="bg-red-700 hover:bg-red-800">
                {editCaseForm.document_type !== originalDocumentType
                  ? 'Μετάβαση για νέο υπολογισμό ημερομηνιών' 
                  : 'Αποθήκευση'
                }
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteEntryId} onOpenChange={() => setDeleteEntryId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Είστε σίγουροι;</AlertDialogTitle>
            <AlertDialogDescription>
              Αυτή η ενέργεια δεν μπορεί να αναιρεθεί. Η εγγραφή θα διαγραφεί μόνιμα.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Διαγραφή
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Case Confirmation Dialog */}
      <AlertDialog open={deleteCaseConfirm} onOpenChange={setDeleteCaseConfirm}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Διαγραφή υπόθεσης</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-900">
              Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την υπόθεση; 
              Θα διαγραφούν όλες οι σχετικές εγγραφές ({localEntries.filter(e => !e.isPotential).length} εγγραφές).
              Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingCase}>Ακύρωση</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCase}
              disabled={isDeletingCase}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {isDeletingCase ? 'Διαγραφή...' : 'Διαγραφή υπόθεσης'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Postponement Wizard */}
      <PostponementWizard
        isOpen={showPostponementWizard}
        onClose={() => {
          setShowPostponementWizard(false);
          setPostponementEntry(null);
        }}
        entry={postponementEntry}
        entries={localEntries}
        cases={[]}
        onRefresh={onRefresh}
      />
    </Dialog>
  );
};

export default EntryDetail;
